export interface TextMatch {
  start: number;
  end: number;
}

type IndexedSymbol = {
  value: string;
  endOffset: number;
};

/**
 * Fold text for case-insensitive Unicode search
 * Uses NFD and locale lowercase so canonically equivalent forms compare equally
 */
export function toCaseInsensitiveSearchText(value: string) {
  return value.normalize('NFD').toLocaleLowerCase();
}

function isCombiningMark(value: string) {
  return /\p{M}/u.test(value);
}

function isCased(value: string) {
  return value.toLocaleLowerCase() !== value.toLocaleUpperCase();
}

function isFinalSigma(symbols: IndexedSymbol[], index: number) {
  let hasCasedBefore = false;
  for (let i = index - 1; i >= 0; i--) {
    const previous = symbols[i]?.value;
    if (!previous) continue;
    if (isCombiningMark(previous)) continue;
    hasCasedBefore = isCased(previous);
    break;
  }

  if (!hasCasedBefore) return false;

  for (let i = index + 1; i < symbols.length; i++) {
    const next = symbols[i]?.value;
    if (!next) continue;
    if (isCombiningMark(next)) continue;
    return !isCased(next);
  }

  return true;
}

function foldSymbolWithContext(symbols: IndexedSymbol[], index: number) {
  const symbol = symbols[index]?.value ?? '';
  if (symbol === 'Σ' && isFinalSigma(symbols, index)) {
    return 'ς';
  }

  return toCaseInsensitiveSearchText(symbol);
}

/**
 * Build folded text and a map from folded boundaries to original UTF-16 offsets
 * Invariant: foldedBoundaryToOriginalOffset[i] is the original end offset at folded boundary i
 */
function buildCaseFoldBoundaryMap(value: string) {
  const symbols: IndexedSymbol[] = [];
  let originalOffset = 0;
  for (const symbol of value) {
    originalOffset += symbol.length;
    symbols.push({ value: symbol, endOffset: originalOffset });
  }

  let foldedText = '';
  const foldedBoundaryToOriginalOffset: number[] = [0];

  for (let i = 0; i < symbols.length; i++) {
    const symbol = symbols[i];
    if (!symbol) continue;
    const foldedSymbol = foldSymbolWithContext(symbols, i);
    const { endOffset } = symbol;

    if (foldedSymbol.length === 0) {
      // Some symbols can disappear after folding; keep the current folded
      // boundary mapped to the latest original offset
      foldedBoundaryToOriginalOffset[
        foldedBoundaryToOriginalOffset.length - 1
      ] = endOffset;
      continue;
    }

    foldedText += foldedSymbol;
    for (let j = 0; j < foldedSymbol.length; j++) {
      // Each folded code-unit boundary maps to the end of the source symbol
      foldedBoundaryToOriginalOffset.push(endOffset);
    }
  }

  return { foldedText, foldedBoundaryToOriginalOffset };
}

/**
 * Find non-overlapping matches and return UTF-16 ranges in the original text
 * Case-insensitive mode uses Unicode folding while keeping offsets valid for Range APIs
 */
export function findTextMatches(
  text: string,
  query: string,
  caseSensitive: boolean
) {
  if (!query) return [];

  if (caseSensitive) {
    const matches: TextMatch[] = [];
    let startPos = 0;
    while (startPos < text.length) {
      const idx = text.indexOf(query, startPos);
      if (idx === -1) break;
      matches.push({ start: idx, end: idx + query.length });
      startPos = idx + query.length;
    }
    return matches;
  }

  const foldedQuery = toCaseInsensitiveSearchText(query);
  if (!foldedQuery) return [];

  const { foldedText, foldedBoundaryToOriginalOffset } =
    buildCaseFoldBoundaryMap(text);

  const matches: TextMatch[] = [];
  let startPos = 0;
  while (startPos < foldedText.length) {
    const idx = foldedText.indexOf(foldedQuery, startPos);
    if (idx === -1) break;

    const start = foldedBoundaryToOriginalOffset[idx];
    const end = foldedBoundaryToOriginalOffset[idx + foldedQuery.length];

    if (end > start) {
      matches.push({ start, end });
      startPos = idx + foldedQuery.length;
    } else {
      // Defensive: avoid infinite loops for degenerate folded offsets
      startPos = idx + 1;
    }
  }

  return matches;
}
